/**
 * ReorderSegmentsModal - Drag-and-drop modal for reordering segments
 *
 * Allows users to reorder segments within a switch step using drag-and-drop.
 * The default segment is displayed but cannot be moved (always stays rightmost).
 *
 * Features:
 * - Drag-and-drop reordering using react-beautiful-dnd
 * - Visual segment preview with color indicators
 * - Filter summary for each segment
 * - Default segment is locked in place
 * - Save/Cancel buttons
 * - ESC key to close
 *
 * Related:
 *   - hooks/useReorderSegmentsModal.ts - State management
 *   - components/ReorderSegmentItem.tsx - Individual item
 *   - utils/segmentReordering.ts - Reorder logic
 */

import { css } from 'aphrodite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DragDropContext, Droppable, type DropResult } from 'react-beautiful-dnd';
import type { SegmentInfo } from '../hooks/useReorderSegmentsModal';
import { ReorderSegmentItem } from './ReorderSegmentItem';
import { styleSheet } from './styles/ReorderSegmentsModalStyles';

interface ReorderSegmentsModalProps {
	/** Whether the modal is currently open */
	isOpen: boolean;
	/** Callback to close the modal */
	onClose: () => void;
	/** Ordered list of segments to display */
	segments: SegmentInfo[];
	/** Callback with new segment IDs order when save is clicked */
	onReorder: (newOrder: string[]) => void;
}

/**
 * Close icon for the modal header
 */
function CloseIcon() {
	return (
		<svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
			<path
				d='M15 5L5 15M5 5l10 10'
				stroke='currentColor'
				strokeWidth='1.5'
				strokeLinecap='round'
				strokeLinejoin='round'
			/>
		</svg>
	);
}

export function ReorderSegmentsModal({
	isOpen,
	onClose,
	segments: initialSegments,
	onReorder,
}: ReorderSegmentsModalProps) {
	const modalRef = useRef<HTMLDivElement>(null);
	const [localSegments, setLocalSegments] = useState<SegmentInfo[]>(initialSegments);

	// Sync local state when segments prop changes
	useEffect(() => {
		setLocalSegments(initialSegments);
	}, [initialSegments]);

	// Close on ESC key
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	// Handle drag end - reorder segments locally
	const onDragEnd = useCallback((result: DropResult) => {
		if (!result.destination) {
			return;
		}

		const sourceIndex = result.source.index;
		const destinationIndex = result.destination.index;

		if (sourceIndex === destinationIndex) {
			return;
		}

		setLocalSegments(prevSegments => {
			// Find the default segment - it should not be in the draggable area
			const filteredNonDefault = prevSegments.filter(s => !s.isDefault);
			const foundDefault = prevSegments.find(s => s.isDefault);

			// Reorder non-default segments
			const reordered = [...filteredNonDefault];
			const [removed] = reordered.splice(sourceIndex, 1);
			reordered.splice(destinationIndex, 0, removed);

			// Add default segment back at the end
			if (foundDefault) {
				reordered.push(foundDefault);
			}

			return reordered;
		});
	}, []);

	// Handle save - emit new order
	const handleSave = useCallback(() => {
		const newOrder = localSegments.map(s => s.id);
		onReorder(newOrder);
		onClose();
	}, [localSegments, onReorder, onClose]);

	// Handle cancel
	const handleCancel = useCallback(() => {
		setLocalSegments(initialSegments);
		onClose();
	}, [initialSegments, onClose]);

	// Handle overlay click
	const handleOverlayClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				handleCancel();
			}
		},
		[handleCancel]
	);

	if (!isOpen) {
		return null;
	}

	// Separate default and non-default segments for rendering
	const nonDefaultSegments = localSegments.filter(s => !s.isDefault);
	const defaultSegment = localSegments.find(s => s.isDefault);

	const modalContent = (
		<div className={css(styleSheet.overlay)} onClick={handleOverlayClick}>
			<div
				ref={modalRef}
				className={css(styleSheet.modal)}
				role='dialog'
				aria-modal='true'
				aria-labelledby='reorder-segments-title'
			>
				{/* Header */}
				<div className={css(styleSheet.header)}>
					<h2 id='reorder-segments-title' className={css(styleSheet.title)}>
						Reorder Segments
					</h2>
					<button type='button' className={css(styleSheet.closeButton)} onClick={handleCancel} aria-label='Close'>
						<CloseIcon />
					</button>
				</div>

				{/* Description */}
				<p className={css(styleSheet.description)}>
					Drag and drop segments to reorder them. The default segment will always remain at the end.
				</p>

				{/* Drag and drop list */}
				<DragDropContext onDragEnd={onDragEnd}>
					<div className={css(styleSheet.body)}>
						<Droppable droppableId='segments-list' direction='vertical'>
							{droppableProvided => (
								<div
									ref={droppableProvided.innerRef}
									{...droppableProvided.droppableProps}
									className={css(styleSheet.list)}
								>
									{nonDefaultSegments.map((segment, index) => (
										<ReorderSegmentItem key={segment.id} segment={segment} index={index} />
									))}
									{droppableProvided.placeholder}
								</div>
							)}
						</Droppable>

						{/* Default segment - always shown at bottom, not draggable */}
						{defaultSegment && (
							<div className={css(styleSheet.defaultSegmentContainer)}>
								<div className={css(styleSheet.defaultSegmentDivider)}>
									<span className={css(styleSheet.defaultSegmentLabel)}>Always last</span>
								</div>
								<ReorderSegmentItem segment={defaultSegment} index={nonDefaultSegments.length} />
							</div>
						)}
					</div>
				</DragDropContext>

				{/* Footer */}
				<div className={css(styleSheet.footer)}>
					<button type='button' className={css(styleSheet.button, styleSheet.buttonSecondary)} onClick={handleCancel}>
						Cancel
					</button>
					<button type='button' className={css(styleSheet.button, styleSheet.buttonPrimary)} onClick={handleSave}>
						Save Order
					</button>
				</div>
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
}
